<template>
  <div class="auth">
    <section class="auth-brand">
      <div class="auth-brand__main">
        <div class="auth-brand__eyebrow">ZHENGJIA DIGITAL</div>
        <h1>简洁购买，<br>稳定交付。</h1>
        <p>数字商品、自动授权与售后服务，都在一个清晰可靠的账户中完成。</p>
      </div>
      <div class="auth-brand__foot">SECURE · CLEAR · AUTOMATED</div>
    </section>
    <div class="card">
      <div class="title">{{ $t('auth.loginTitle') }}</div>
      <div class="sub">{{ $t('auth.loginSub') }}</div>

      <el-form ref="form" :model="form" :rules="rules" label-position="top" class="form">
        <el-form-item :label="$t('auth.username')" prop="username">
          <el-input v-model.trim="form.username" :placeholder="$t('auth.usernamePh')" autocomplete="username" />
        </el-form-item>

        <el-form-item :label="$t('auth.password')" prop="password">
          <el-input
            v-model="form.password"
            :placeholder="$t('auth.passwordPh')"
            autocomplete="current-password"
            show-password
          />
        </el-form-item>

        <el-form-item :label="$t('auth.captcha')" prop="captcha">
          <div class="captchaRow">
            <el-input v-model.trim="form.captcha" :placeholder="$t('auth.captchaPh')" autocomplete="off" />
            <button type="button" class="captchaImg" @click="refreshCaptcha" :title="$t('auth.refreshCaptcha')">
              <img :src="captchaUrl" alt="captcha" />
            </button>
          </div>
        </el-form-item>

        <el-button type="primary" class="submit" :loading="loading" @click="submit">
          {{ $t('auth.loginBtn') }}
        </el-button>

        <div class="footer">
          <span class="muted">{{ $t('auth.noAccount') }}</span>
          <el-button type="text" @click="goRegister">{{ $t('auth.goRegister') }}</el-button>
        </div>
      </el-form>
    </div>
  </div>
</template>

<script>
import { drawCaptchaDataUrl, randomCaptchaCode } from '@/utils/captcha'
import { loginUser, refreshProfileFromServer } from '@/utils/auth'

export default {
  name: 'LoginPage',
  data() {
    return {
      loading: false,
      captchaCode: '',
      captchaUrl: '',
      form: {
        username: '',
        password: '',
        captcha: ''
      },
      rules: {
        username: [{ required: true, message: this.$t('auth.usernameReq'), trigger: 'blur' }],
        password: [{ required: true, message: this.$t('auth.passwordReq'), trigger: 'blur' }],
        captcha: [{ required: true, message: this.$t('auth.captchaReq'), trigger: 'blur' }]
      }
    }
  },
  created() {
    this.refreshCaptcha()
  },
  methods: {
    refreshCaptcha() {
      this.captchaCode = randomCaptchaCode(4)
      this.captchaUrl = drawCaptchaDataUrl(this.captchaCode)
    },
    goRegister() {
      this.$router.push('/register')
    },
    async submit() {
      const ok = await this.$refs.form.validate().catch(() => false)
      if (!ok) return

      const input = String(this.form.captcha || '').trim().toUpperCase()
      if (input !== String(this.captchaCode).toUpperCase()) {
        this.$message.error(this.$t('auth.captchaInvalid'))
        this.form.captcha = ''
        this.refreshCaptcha()
        return
      }

      this.loading = true
      try {
        const res = await loginUser({ username: this.form.username, password: this.form.password })
        if (!res.ok) {
          if (res.message === 'USER_NOT_FOUND') this.$message.error(this.$t('auth.userNotFound'))
          else if (res.message === 'INVALID_PASSWORD') this.$message.error(this.$t('auth.passwordInvalid'))
          else if (res.message === 'ACCOUNT_LOCKED') this.$message.error(this.$t('auth.accountLocked'))
          else if (res.message === 'RATE_LIMITED') this.$message.error(this.$t('auth.rateLimited'))
          else if (res.message === 'NETWORK_ERROR') this.$message.error('无法连接服务器，请确认后端已启动')
          else this.$message.error(this.$t('auth.loginFailed'))
          this.refreshCaptcha()
          return
        }
        this.$message.success(this.$t('auth.loginOk'))
        await refreshProfileFromServer()
        const red = this.$route.query.redirect
        if (typeof red === 'string' && red.startsWith('/')) {
          this.$router.replace(red)
        } else {
          this.$router.replace('/home')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
<style scoped>
.auth {
  height: 100vh;
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(820px 460px at 12% 18%, rgba(226, 232, 240, 0.72), transparent 58%),
    radial-gradient(720px 440px at 88% 16%, rgba(241, 245, 249, 0.92), transparent 56%),
    radial-gradient(680px 420px at 50% 100%, rgba(229, 231, 235, 0.75), transparent 60%),
    linear-gradient(135deg, #ffffff 0%, #fcfcfd 38%, #f7f8fa 72%, #ffffff 100%);
}

.auth::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(rgba(15, 23, 42, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 23, 42, 0.025) 1px, transparent 1px);
  background-size: 76px 76px;
  mask-image: radial-gradient(circle at center, black, transparent 80%);
  opacity: 0.42;
  pointer-events: none;
}

.card {
  width: 100%;
  max-width: 430px;
  border-radius: 24px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.92));
  backdrop-filter: blur(20px);
  box-shadow:
    0 24px 60px rgba(15, 23, 42, 0.08),
    0 8px 20px rgba(15, 23, 42, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  padding: 28px 24px 20px;
  position: relative;
  z-index: 1;
}

.card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(226, 232, 240, 0.35));
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.title {
  font-size: 24px;
  font-weight: 900;
  color: #0f172a;
}

.sub {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(71, 85, 105, 0.72);
}

.form {
  margin-top: 18px;
}

.form :deep(.el-form-item__label) {
  color: rgba(30, 41, 59, 0.86);
  font-weight: 600;
}

.form :deep(.el-input__inner) {
  height: 44px;
  border-radius: 14px;
  border-color: rgba(148, 163, 184, 0.22);
  background: rgba(255, 255, 255, 0.92);
  color: #0f172a;
}

.form :deep(.el-input__inner:focus) {
  border-color: rgba(59, 130, 246, 0.42);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.10);
}

.form :deep(.el-input__inner::placeholder) {
  color: rgba(148, 163, 184, 0.9);
}

.captchaRow {
  display: grid;
  grid-template-columns: 1fr 112px;
  gap: 10px;
  align-items: center;
}

.captchaImg {
  height: 44px;
  padding: 0;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  overflow: hidden;
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
}

.captchaImg:hover {
  transform: translateY(-1px);
  border-color: rgba(59, 130, 246, 0.26);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.captchaImg img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.submit {
  width: 100%;
  margin-top: 10px;
}

.footer {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.muted {
  color: rgba(100, 116, 139, 0.72);
}

.footer :deep(.el-button--text) {
  color: #2563eb;
}

@media (max-width: 640px) {
  .auth {
    padding: 16px;
  }

  .card {
    max-width: none;
    padding: 22px 18px 18px;
  }
}
</style>
<style scoped>
.auth{display:grid;grid-template-columns:minmax(360px,.9fr) minmax(440px,1.1fr);padding:0;background:#f5f8f9}.auth::before{display:none}.auth-brand{display:flex;flex-direction:column;min-height:100vh;padding:52px 9vw 44px 7vw;background:radial-gradient(circle at 75% 15%,rgba(126,181,192,.28),transparent 28%),#173f4b;color:#fff}.auth-brand__main{flex:1;display:flex;flex-direction:column;justify-content:center}.auth-brand__eyebrow{margin-bottom:18px;color:#a9cbd2;font-size:10px;font-weight:800;letter-spacing:.2em}.auth-brand h1{margin:0;font-size:clamp(42px,5vw,66px);line-height:1.08;letter-spacing:-.045em}.auth-brand p{max-width:470px;margin:22px 0 0;color:rgba(255,255,255,.62);font-size:14px;line-height:1.9}.auth-brand__foot{color:rgba(255,255,255,.42);font-size:9px;letter-spacing:.18em}.card{justify-self:center;max-width:440px;padding:34px 32px 28px;border:1px solid #dce7e9;border-radius:18px;background:#fff;box-shadow:0 24px 70px rgba(24,63,74,.09);backdrop-filter:none}.card::before{display:none}.title{color:#102a32;font-size:27px;letter-spacing:-.02em}.sub{color:#71858a}.form :deep(.el-input__inner){height:46px;border-color:#dce7e9;border-radius:9px}.form :deep(.el-input__inner:focus){border-color:#2d6a78;box-shadow:0 0 0 3px rgba(45,106,120,.09)}.captchaImg{height:46px;border-color:#dce7e9;border-radius:9px}@media(max-width:850px){.auth{display:flex;padding:18px}.auth-brand{display:none}.card{max-width:440px}}@media(max-width:520px){.card{padding:28px 20px 22px}}
</style>

